// src/views/RoutesView.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db, functions } from '../firebase/config';
import { collection, query, where, onSnapshot, orderBy, doc, setDoc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { can, PERMISSIONS } from '../utils/permissions';
import { validateAddressViaCF } from '../utils/address';
import { getJobArrivalWindowLabel } from '../utils/arrivalWindows'; // aw-rts-6f708192 // aw-seam4
import {
    Map, Navigation, RotateCcw, Zap, ChevronDown, ChevronUp,
    AlertTriangle, CheckCircle, Clock, MapPin, Users, Calendar,
    GripVertical, Save, ExternalLink, Eye, EyeOff, Info, BookOpen,
    Timer, ArrowRight, Home, Crosshair, Edit3, X as XIcon,
    Loader2,
} from 'lucide-react';
import { format, startOfDay, endOfDay } from 'date-fns';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const CREW_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
    '#8b5cf6', '#ec4899', '#14b8a6', '#6366f1',
];


const toDateInput = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toISOString().split('T')[0];
};

// ─────────────────────────────────────────────────────────────────────────────
// STARTING POINT BAR
// Chip-style display of the current starting point + inline picker with three
// options: route group's saved default, browser geolocation, or custom address.
// ─────────────────────────────────────────────────────────────────────────────
const StartingPointBar = ({ startingPoint, groupDepot, onChange, onClear, canManage, functions }) => {
    const [open, setOpen]                 = useState(false);
    const [customAddress, setCustomAddress] = useState('');
    const [geoLoading, setGeoLoading]     = useState(false);
    const [geoError, setGeoError]         = useState('');

    // ── Verification state for the custom-address flow (Phase 3) ──────────
    // Status flow:
    //   idle             — input is empty or untouched
    //   checking         — validateAddress CF call in flight
    //   ok               — verified, canonical matches typed (auto-commit)
    //   ok_with_changes  — verified, but Google adjusted something (user chooses)
    //   ambiguous        — multiple plausible matches (user picks one)
    //   not_found        — Google found nothing (user can save anyway)
    //   error            — transient API/rate-limit failure (auto-commit raw)
    const [verifyState, setVerifyState] = useState({
        status: 'idle',
        canonical: null,
        coords: null,
        placeId: null,
        alternatives: null,
        differences: null,
        message: null,
    });

    // Reset verification state whenever the picker closes/opens fresh
    useEffect(() => {
        if (!open) {
            setVerifyState({
                status: 'idle', canonical: null, coords: null, placeId: null,
                alternatives: null, differences: null, message: null,
            });
            setCustomAddress('');
            setGeoError('');
        }
    }, [open]);

    const handlePickDepot = () => {
        if (!groupDepot) return;
        onChange({ mode: 'depot', address: groupDepot, label: groupDepot });
        setOpen(false);
        setGeoError('');
    };

    const handlePickCurrent = () => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            setGeoError('Geolocation is not supported in this browser.');
            return;
        }
        setGeoLoading(true);
        setGeoError('');
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                onChange({
                    mode: 'current',
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                    label: 'My current location',
                });
                setGeoLoading(false);
                setOpen(false);
            },
            (err) => {
                setGeoError(
                    err.code === 1 ? 'Location permission denied. Allow access in your browser settings.' :
                    err.code === 2 ? 'Could not determine your location. Try again or use a typed address.' :
                    err.code === 3 ? 'Location request timed out. Try again.' :
                    'Could not get your location.'
                );
                setGeoLoading(false);
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
        );
    };

    // Build the starting-point payload from a verified result and commit it
    // to the parent. Used for ok and after-user-accepts cases.
    const commitVerified = (addr, canonical, coords, placeId) => {
        onChange({
            mode: 'custom',
            address: addr,
            label: canonical || addr,
            ...(coords && typeof coords.lat === 'number' ? {
                lat: coords.lat,
                lng: coords.lng,
            } : {}),
            ...(placeId ? { placeId } : {}),
            verified: true,
        });
        setOpen(false);
    };

    // Commit the user's typed address as-is, no verification. Used when:
    //   - status is 'not_found' and user chooses to save anyway
    //   - status is 'error' (API down) — soft fail
    //   - status is 'ambiguous' / 'ok_with_changes' and user clicks "Use what I typed"
    const commitUnverified = (addr) => {
        onChange({
            mode: 'custom',
            address: addr,
            label: addr,
            verified: false,
        });
        setOpen(false);
    };

    const handlePickCustom = async () => {
        const addr = customAddress.trim();
        if (!addr) { setGeoError('Enter a starting address.'); return; }
        setGeoError('');
        setVerifyState({
            status: 'checking', canonical: null, coords: null, placeId: null,
            alternatives: null, differences: null, message: null,
        });

        try {
            const result = await validateAddressViaCF(functions, null, addr);
            if (result.status === 'ok') {
                // Quick win: auto-commit verified addresses with no adjustments.
                // Only show the diff panel when canonical differs from typed.
                const typedLower    = addr.toLowerCase().replace(/\s+/g, ' ').trim();
                const canonLower    = (result.canonical || '').toLowerCase().replace(/\s+/g, ' ').trim();
                // Quick heuristic: if canonical strictly contains typed, treat as
                // no adjustment. Otherwise, surface so user can review.
                if (canonLower.includes(typedLower)) {
                    commitVerified(addr, result.canonical, result.coords, result.placeId);
                } else {
                    setVerifyState({
                        status: 'ok_with_changes',
                        canonical: result.canonical,
                        coords:    result.coords,
                        placeId:   result.placeId,
                        alternatives: null,
                        differences: null,
                        message: null,
                    });
                }
            } else if (result.status === 'ambiguous') {
                setVerifyState({
                    status: 'ambiguous',
                    canonical: null, coords: null, placeId: null,
                    alternatives: result.alternatives || [],
                    differences: null,
                    message: null,
                });
            } else if (result.status === 'not_found') {
                setVerifyState({
                    status: 'not_found',
                    canonical: null, coords: null, placeId: null,
                    alternatives: null, differences: null,
                    message: "We couldn't find that address.",
                });
            } else {
                // 'error' from CF — soft fail, commit as-typed without verification
                commitUnverified(addr);
            }
        } catch (err) {
            // Rate limit or network error — soft fail
            commitUnverified(addr);
        }
    };

    // User clicked one of the ambiguous alternatives — commit it as the
    // starting point, using canonical + coords from Google directly.
    const handlePickCustomAlternative = (alt) => {
        commitVerified(alt.canonical, alt.canonical, alt.coords, alt.placeId);
    };

    // ok_with_changes: user accepted Google's canonical
    const handleAcceptCanonical = () => {
        commitVerified(verifyState.canonical, verifyState.canonical, verifyState.coords, verifyState.placeId);
    };

    // ok_with_changes: user kept what they typed
    const handleKeepTyped = () => {
        commitVerified(customAddress.trim(), verifyState.canonical, verifyState.coords, verifyState.placeId);
    };

    // not_found: user wants to save the unverifiable address anyway
    const handleSaveAnyway = () => {
        commitUnverified(customAddress.trim());
    };

    // not_found: user wants to try a different address
    const handleTryAgain = () => {
        setVerifyState({
            status: 'idle', canonical: null, coords: null, placeId: null,
            alternatives: null, differences: null, message: null,
        });
    };

    const handleCustomKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handlePickCustom(); }
    };

    // ── Render: chip (collapsed) ────────────────────────────────────────────
    if (!open) {
        const isSet = !!startingPoint;
        const label = startingPoint?.label
            || (startingPoint?.mode === 'current' ? 'My current location' : startingPoint?.address)
            || '';
        const modeIcon =
            startingPoint?.mode === 'depot'   ? <Home size={13} className="text-blue-600" /> :
            startingPoint?.mode === 'current' ? <Crosshair size={13} className="text-blue-600" /> :
            startingPoint?.mode === 'custom'  ? <Edit3 size={13} className="text-blue-600" /> :
            <AlertTriangle size={13} className="text-amber-600" />;

        return (
            <div className={`mb-4 rounded-lg border px-3 py-2 flex items-center justify-between gap-3 ${
                isSet ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'
            }`}>
                <div className="flex items-center gap-2 min-w-0">
                    {modeIcon}
                    <div className="min-w-0">
                        {isSet ? (
                            <>
                                <p className="text-xs font-medium text-gray-500 leading-tight">Optimizing from</p>
                                <p className="text-sm font-semibold text-gray-800 truncate" title={label}>{label}</p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-semibold text-amber-800 leading-tight">Starting point not set</p>
                                <p className="text-xs text-amber-700">First scheduled stop will be used as the origin.</p>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {isSet && canManage && (
                        <button
                            onClick={onClear}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                            title="Clear starting point"
                        >
                            <XIcon size={14} />
                        </button>
                    )}
                    {canManage && (
                        <button
                            onClick={() => setOpen(true)}
                            className="text-xs font-semibold px-2.5 py-1 rounded-md bg-white border border-gray-300 hover:bg-gray-50 transition-colors text-gray-700"
                        >
                            {isSet ? 'Change' : 'Set starting point'}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // ── Render: picker (expanded) ───────────────────────────────────────────
    return (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 space-y-3 shadow-sm">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-semibold text-gray-800">Starting Point</p>
                    <p className="text-xs text-gray-500">Where this crew is leaving from. Determines the first stop.</p>
                </div>
                <button
                    onClick={() => { setOpen(false); setGeoError(''); }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                >
                    <XIcon size={16} />
                </button>
            </div>

            {/* Option 1: route group default */}
            <button
                type="button"
                onClick={handlePickDepot}
                disabled={!groupDepot}
                className={`w-full text-left rounded-lg border px-3 py-2.5 flex items-start gap-2.5 transition-colors ${
                    groupDepot
                        ? 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                }`}
            >
                <Home size={15} className={`mt-0.5 flex-shrink-0 ${groupDepot ? 'text-blue-600' : 'text-gray-400'}`} />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">Default starting address</p>
                    {groupDepot ? (
                        <p className="text-xs text-gray-500 truncate" title={groupDepot}>{groupDepot}</p>
                    ) : (
                        <p className="text-xs text-gray-400">
                            Not set. Add one in <strong>Settings → Route Groups</strong> to reuse across all optimizations.
                        </p>
                    )}
                </div>
            </button>

            {/* Option 2: current location */}
            <button
                type="button"
                onClick={handlePickCurrent}
                disabled={geoLoading}
                className="w-full text-left rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 px-3 py-2.5 flex items-start gap-2.5 transition-colors disabled:opacity-60"
            >
                <Crosshair size={15} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                        {geoLoading ? 'Getting your location…' : 'Use my current location'}
                    </p>
                    <p className="text-xs text-gray-500">
                        Requests location permission from your browser.
                    </p>
                </div>
            </button>

            {/* Option 3: custom address */}
            <div className="rounded-lg border border-gray-200 px-3 py-2.5">
                <div className="flex items-start gap-2.5">
                    <Edit3 size={15} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">Custom address</p>
                        <p className="text-xs text-gray-500 mb-2">For today only — enter a full street address.</p>

                        {/* Input + Use button — hidden while showing verification results */}
                        {(verifyState.status === 'idle' || verifyState.status === 'checking') && (
                            <div className="flex items-center gap-2">
                                <input
                                    value={customAddress}
                                    onChange={e => {
                                        setCustomAddress(e.target.value);
                                        setGeoError('');
                                        if (verifyState.status !== 'idle') {
                                            setVerifyState({
                                                status: 'idle', canonical: null, coords: null,
                                                placeId: null, alternatives: null,
                                                differences: null, message: null,
                                            });
                                        }
                                    }}
                                    onKeyDown={handleCustomKeyDown}
                                    placeholder="e.g. 100 Algoma St, Thunder Bay, ON"
                                    disabled={verifyState.status === 'checking'}
                                    className="flex-1 min-w-0 border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                                />
                                <button
                                    type="button"
                                    onClick={handlePickCustom}
                                    disabled={verifyState.status === 'checking'}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-60 transition-colors flex-shrink-0"
                                >
                                    {verifyState.status === 'checking' ? (
                                        <>
                                            <Loader2 size={13} className="animate-spin" />
                                            <span>Checking</span>
                                        </>
                                    ) : 'Use'}
                                </button>
                            </div>
                        )}

                        {/* ok_with_changes — Google adjusted; user chooses */}
                        {verifyState.status === 'ok_with_changes' && (
                            <div className="bg-amber-50 border border-amber-200 rounded p-2.5 space-y-2.5 mt-1">
                                <div className="flex items-start gap-2 text-xs text-amber-800">
                                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold mb-1">Google found a different address</div>
                                        <div className="text-amber-700">
                                            <div className="line-through text-amber-500 break-words">{customAddress.trim()}</div>
                                            <div className="font-semibold flex items-start gap-1 mt-0.5">
                                                <MapPin size={11} className="flex-shrink-0 mt-0.5" />
                                                <span className="break-words">{verifyState.canonical}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleAcceptCanonical}
                                        className="flex-1 px-2 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded hover:bg-amber-700 transition-colors"
                                    >
                                        Use Google's version
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleKeepTyped}
                                        className="flex-1 px-2 py-1.5 bg-white border border-amber-300 text-amber-700 text-xs font-semibold rounded hover:bg-amber-100 transition-colors"
                                    >
                                        Use what I typed
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ambiguous — user picks an alternative */}
                        {verifyState.status === 'ambiguous' && verifyState.alternatives?.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded p-2.5 space-y-2 mt-1">
                                <div className="flex items-center gap-2 text-xs text-amber-800 font-semibold">
                                    <AlertTriangle size={13} />
                                    <span>Did you mean one of these?</span>
                                </div>
                                <div className="space-y-1">
                                    {verifyState.alternatives.map((alt, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => handlePickCustomAlternative(alt)}
                                            className="w-full text-left text-xs px-2 py-1.5 bg-white border border-amber-200 rounded hover:border-amber-400 hover:bg-amber-50 flex items-start gap-2"
                                        >
                                            <MapPin size={11} className="flex-shrink-0 mt-0.5 text-amber-600" />
                                            <span className="break-words flex-1">{alt.canonical}</span>
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleTryAgain}
                                    className="text-xs text-amber-700 underline hover:text-amber-900"
                                >
                                    None of these — try a different address
                                </button>
                            </div>
                        )}

                        {/* not_found — let user save anyway or retry */}
                        {verifyState.status === 'not_found' && (
                            <div className="bg-red-50 border border-red-200 rounded p-2.5 space-y-2.5 mt-1">
                                <div className="flex items-start gap-2 text-xs text-red-700">
                                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <div className="font-semibold">Address not found</div>
                                        <div className="text-red-600 mt-0.5 break-words">"{customAddress.trim()}" — {verifyState.message}</div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveAnyway}
                                        className="flex-1 px-2 py-1.5 bg-white border border-red-300 text-red-700 text-xs font-semibold rounded hover:bg-red-100 transition-colors"
                                    >
                                        Use it anyway
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleTryAgain}
                                        className="flex-1 px-2 py-1.5 bg-red-600 text-white text-xs font-semibold rounded hover:bg-red-700 transition-colors"
                                    >
                                        Try a different address
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Error */}
            {geoError && (
                <div className="flex items-center gap-2 text-red-600 text-xs">
                    <AlertTriangle size={13} />
                    <span>{geoError}</span>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ROUTE HEALTH CARD
// ─────────────────────────────────────────────────────────────────────────────
const RouteHealthCard = ({ health, onDismiss }) => {
    if (!health) return null;
    const { improvementPct, timeSavedMinutes, originalDistanceKm, optimizedDistanceKm } = health;

    if (originalDistanceKm === 0 && optimizedDistanceKm === 0) {
        return (
            <div className="rounded-xl border bg-amber-50 border-amber-200 p-4 mb-5">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-semibold text-amber-800">Could not calculate drive times</p>
                            <p className="text-xs text-amber-700 mt-1">
                                Job addresses could not be geocoded. Make sure jobs have full street addresses including city,
                                and that the Geocoding API is enabled on your backend API key.
                            </p>
                        </div>
                    </div>
                    <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <ChevronUp size={16} />
                    </button>
                </div>
            </div>
        );
    }

    const bigSaving = improvementPct >= 10 && timeSavedMinutes >= 5;
    const startLabel = health.startingPointUsed && health.startingPointLabel
        ? health.startingPointLabel : null;

    return (
        <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-4 mb-5">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-semibold text-emerald-800">
                            Route optimized
                            {bigSaving && ` · ${timeSavedMinutes} min saved (${improvementPct}% shorter)`}
                        </p>
                        <p className="text-xs text-emerald-700 mt-1">
                            Total drive: <strong>{optimizedDistanceKm} km</strong>
                            {startLabel && <> from <strong>{startLabel}</strong></>}
                            {bigSaving && ` · Down from ${originalDistanceKm} km in original booking order (${improvementPct}% improvement)`}
                            {!bigSaving && originalDistanceKm > 0 && ` · Compared to ${originalDistanceKm} km in original order`}
                        </p>
                    </div>
                </div>
                <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                    <ChevronUp size={16} />
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// JOB STOP CARD
// ─────────────────────────────────────────────────────────────────────────────
const JobStopCard = ({ job, index, color, isDragging, onJobClick }) => {
    const scheduleLabel = job.schedulingMode === 'window'
        ? getJobArrivalWindowLabel(job)
        : job.startAt
            ? format(job.startAt instanceof Date ? job.startAt : job.startAt.toDate(), 'h:mm a')
            : null;

    // Calculate duration from endAt - startAt
    const getDuration = () => {
        const start = job.startAt?.toDate ? job.startAt.toDate() : (job.startAt instanceof Date ? job.startAt : null);
        const end   = job.endAt?.toDate   ? job.endAt.toDate()   : (job.endAt   instanceof Date ? job.endAt   : null);
        if (!start || !end) return null;
        const mins = Math.round((end - start) / 60000);
        if (mins <= 0) return null;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
    };
    const duration = getDuration();

    // Status indicator dot
    const statusDot = job.status === 'Completed'  ? 'bg-green-500'
        : job.status === 'In Progress' ? 'bg-blue-500 animate-pulse'
        : null;

    return (
        <div
            onClick={() => onJobClick && onJobClick(job)}
            className={`flex items-start gap-2 p-3 rounded-lg border bg-white transition-all ${
                isDragging ? 'shadow-lg border-blue-300 rotate-1' : 'border-gray-200 hover:border-blue-300 hover:shadow-sm cursor-pointer'
            } ${job.status === 'Completed' ? 'opacity-60' : ''}`}
        >
            <div className="relative flex-shrink-0 mt-0.5">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                     style={{ backgroundColor: color }}>
                    {index + 1}
                </div>
                {statusDot && (
                    <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${statusDot}`} />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-semibold text-gray-800 truncate">{job.title}</p>
                    {job.isMultiCrew && (
                        <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full border"
                            style={{ backgroundColor: job.thisCrewCompleted ? '#dcfce7' : '#eff6ff', color: job.thisCrewCompleted ? '#166534' : '#1d4ed8', borderColor: job.thisCrewCompleted ? '#bbf7d0' : '#bfdbfe' }}
                            title={`Multi-crew job — also assigned to: ${job.otherCrews?.join(', ')}`}
                        >
                            {job.thisCrewCompleted ? '✓ My crew done' : `+${job.multiCrewTotal - 1} crew${job.multiCrewTotal - 1 > 1 ? 's' : ''}`}
                        </span>
                    )}
                </div>
                <p className="text-xs text-gray-500 truncate">{job.clientName}</p>
                {job.address && (
                    <p className="text-xs text-gray-400 truncate flex items-center gap-1 mt-0.5">
                        <MapPin size={10} className="flex-shrink-0" />
                        {job.address}
                    </p>
                )}
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {scheduleLabel && (
                        <p className="text-xs text-blue-600 flex items-center gap-1">
                            <Clock size={10} className="flex-shrink-0" />
                            {scheduleLabel}
                        </p>
                    )}
                    {duration && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                            <Timer size={10} className="flex-shrink-0" />
                            {duration}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex-shrink-0 flex flex-col items-center gap-0.5 mt-1">
                <ArrowRight size={13} className="text-gray-300" />
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// CREW CARD
// ─────────────────────────────────────────────────────────────────────────────
const CrewCard = ({ group, members, jobs, color, driveTimeMinutes, onDragEnd, isOptimized, onJobClick, isExternalDragging, onExternalDrop, startingPoint }) => {
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);
    const [localJobs, setLocalJobs] = useState(jobs);
    const [isExternalDragOver, setIsExternalDragOver] = useState(false);

    useEffect(() => { setLocalJobs(jobs); }, [jobs]);

    const handleDragStart = (idx) => setDragIdx(idx);
    const handleDragOver = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
    const handleDrop = (e, idx) => {
        e.preventDefault();
        // If this is an external (unassigned) drop, let the container handle it
        if (e.dataTransfer.getData('type') === 'unassigned') return;
        if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setOverIdx(null); return; }
        const updated = [...localJobs];
        const [removed] = updated.splice(dragIdx, 1);
        updated.splice(idx, 0, removed);
        setLocalJobs(updated);
        onDragEnd(group.id, updated);
        setDragIdx(null);
        setOverIdx(null);
    };

    const handleMoveStop = (fromIdx, toIdx) => {
        if (toIdx < 0 || toIdx >= localJobs.length) return;
        const updated = [...localJobs];
        const [removed] = updated.splice(fromIdx, 1);
        updated.splice(toIdx, 0, removed);
        setLocalJobs(updated);
        onDragEnd(group.id, updated);
    };

    const buildGoogleMapsUrl = () => {
        const stops = localJobs.filter(j => j.address && j.address.trim() !== '' && j.address.trim() !== ',');
        if (stops.length === 0) return null;

        // Resolve the origin (address string, or "lat,lng" for current location)
        let origin = null;
        if (startingPoint?.address) {
            origin = startingPoint.address;
        } else if (typeof startingPoint?.lat === 'number' && typeof startingPoint?.lng === 'number') {
            origin = `${startingPoint.lat},${startingPoint.lng}`;
        }

        // Build the path-segment URL Google uses internally:
        //   /maps/dir/<origin>/<wp1>/<wp2>/.../<destination>
        // This bypasses ?api=1 parsing entirely, which has been observed to
        // drop the origin parameter when there are many waypoints.
        const segments = [];
        if (origin) segments.push(origin);
        stops.forEach(s => segments.push(s.address));

        if (segments.length === 1) {
            return `https://maps.google.com/?q=${encodeURIComponent(segments[0])}`;
        }

        const path = segments.map(s => encodeURIComponent(s)).join('/');
        return `https://www.google.com/maps/dir/${path}/`;
    };

    const mapsUrl = buildGoogleMapsUrl();

    // ── External drop handlers ──────────────────────────────────────────────
    const handleContainerDragOver = (e) => {
        if (!isExternalDragging) return;
        e.preventDefault();
        setIsExternalDragOver(true);
    };

    const handleContainerDragLeave = (e) => {
        // Only clear if leaving the card entirely (not entering a child)
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsExternalDragOver(false);
        }
    };

    const handleContainerDrop = (e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('type');
        if (type === 'unassigned') {
            const jobId = e.dataTransfer.getData('jobId');
            if (jobId && onExternalDrop) {
                onExternalDrop(jobId, group.id);
            }
        }
        setIsExternalDragOver(false);
    };

    return (
        <div
            className={`rounded-xl border bg-white overflow-hidden transition-all ${
                isExternalDragOver
                    ? 'border-amber-400 shadow-lg ring-2 ring-amber-200'
                    : isExternalDragging
                        ? 'border-amber-200 border-dashed'
                        : 'border-gray-200'
            }`}
            onDragOver={handleContainerDragOver}
            onDragLeave={handleContainerDragLeave}
            onDrop={handleContainerDrop}
        >
            {/* External drag-over overlay */}
            {isExternalDragging && (
                <div className={`px-4 py-2 text-center text-xs font-semibold transition-all ${
                    isExternalDragOver
                        ? 'bg-amber-500 text-white'
                        : 'bg-amber-50 text-amber-700'
                }`}>
                    {isExternalDragOver ? '✓ Drop to assign to this crew' : 'Drop job here to assign to this crew'}
                </div>
            )}

            {/* Card header — group name + member badges + navigate */}
            <div className="px-4 py-3 border-b" style={{ borderLeftWidth: 4, borderLeftColor: color }}>
                <div className="flex items-center justify-between gap-2 mb-2">
                    <div>
                        <p className="text-sm font-semibold text-gray-800">{group.name}</p>
                        <p className="text-xs text-gray-400">
                            {localJobs.length} {localJobs.length === 1 ? 'stop' : 'stops'}
                            {driveTimeMinutes > 0 && ` · ~${driveTimeMinutes} min driving`}
                        </p>
                    </div>
                    {mapsUrl && (
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                               className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                                <Navigation size={13} />
                                Navigate
                            </a>
                            {(localJobs.length + (startingPoint ? 1 : 0)) > 10 && (
                                <p className="text-[10px] text-amber-700 text-right leading-tight max-w-[180px]">
                                    Google Maps caps routes at 10 stops — some may not appear
                                </p>
                            )}
                        </div>
                    )}
                </div>
                {/* Member badges */}
                {members && members.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {members.map(m => (
                            <div key={m.id} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white"
                                 style={{ backgroundColor: color + 'cc' }}>
                                <span>{m.name?.charAt(0).toUpperCase()}</span>
                                <span className="hidden sm:inline">{m.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Starting point marker — shown above the first stop when set */}
            {startingPoint && localJobs.length > 0 && (
                <div className="px-3 pt-3">
                    <div className="flex items-start gap-2 p-2 rounded-lg bg-blue-50 border border-blue-100">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                             style={{ backgroundColor: color }}>
                            {startingPoint.mode === 'current'
                                ? <Crosshair size={12} className="text-white" />
                                : <Home size={12} className="text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-blue-800">Starting from</p>
                            <p className="text-xs text-blue-600 truncate" title={startingPoint.label || startingPoint.address}>
                                {startingPoint.label || startingPoint.address || 'Current location'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Job stop list */}
            <div className="p-3 space-y-2">
                {localJobs.length === 0 && !isExternalDragging && (
                    <p className="text-xs text-gray-400 text-center py-4">No stops assigned yet</p>
                )}
                {localJobs.length === 0 && isExternalDragging && (
                    <p className="text-xs text-gray-400 text-center py-4">Drop a job here to add it to this crew</p>
                )}
                {localJobs.map((job, idx) => {
                    const isDragging = dragIdx === idx;
                    const isOver    = overIdx === idx && dragIdx !== null;
                    return (
                        <div
                            key={job.jobId || job.id}
                            draggable={!isExternalDragging}
                            onDragStart={() => handleDragStart(idx)}
                            onDragOver={(e) => handleDragOver(e, idx)}
                            onDrop={(e) => handleDrop(e, idx)}
                            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                            className={`transition-all ${isOver ? 'translate-y-1 opacity-50' : ''}`}
                        >
                            <div className="flex items-center gap-1.5">
                                {isOptimized && (
                                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                                        <button
                                            onClick={() => handleMoveStop(idx, idx - 1)}
                                            disabled={idx === 0}
                                            className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20 transition-colors"
                                        >
                                            <ChevronUp size={12} />
                                        </button>
                                        <button
                                            onClick={() => handleMoveStop(idx, idx + 1)}
                                            disabled={idx === localJobs.length - 1}
                                            className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20 transition-colors"
                                        >
                                            <ChevronDown size={12} />
                                        </button>
                                    </div>
                                )}
                                <div className={`flex-1 min-w-0 ${!isExternalDragging ? 'cursor-grab' : ''}`}>
                                    <JobStopCard
                                        job={job}
                                        index={idx}
                                        color={color}
                                        isDragging={isDragging}
                                        onJobClick={onJobClick}
                                    />
                                </div>
                            </div>
                            {/* Drive time connector */}
                            {idx < localJobs.length - 1 && (
                                <div className="flex items-center gap-1.5 pl-8 py-1">
                                    <div className="w-px h-3 bg-gray-200 ml-2.5" />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// UNASSIGNED JOBS PANEL
// ─────────────────────────────────────────────────────────────────────────────
const UnassignedJobsPanel = ({ jobs, routeGroups, preferredCrewMap, onDragStart, onDragEnd, canManage }) => {
    const [collapsed, setCollapsed] = useState(false);

    if (jobs.length === 0) return null;

    return (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
            <button
                type="button"
                onClick={() => setCollapsed(p => !p)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-100 transition-colors text-left"
            >
                <div className="flex items-center gap-2">
                    <AlertTriangle size={15} className="text-amber-600 flex-shrink-0" />
                    <span className="text-sm font-semibold text-amber-900">
                        {jobs.length} unassigned {jobs.length === 1 ? 'job' : 'jobs'} — not in any crew route
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {canManage && !collapsed && (
                        <span className="text-xs text-amber-600 font-medium hidden sm:block">
                            Drag to assign
                        </span>
                    )}
                    {collapsed
                        ? <ChevronDown size={16} className="text-amber-500" />
                        : <ChevronUp size={16} className="text-amber-500" />
                    }
                </div>
            </button>

            {!collapsed && (
                <div className="px-4 pb-4 space-y-2">
                    {jobs.map(job => {
                        const preferredCrewId = preferredCrewMap[job.clientId];
                        const preferredCrew = preferredCrewId
                            ? routeGroups.find(g => g.id === preferredCrewId)
                            : null;
                        const scheduleLabel = job.schedulingMode === 'window'
                            ? (getJobArrivalWindowLabel(job))
                            : job.startAt
                                ? format(job.startAt instanceof Date ? job.startAt : job.startAt.toDate(), 'h:mm a')
                                : null;

                        return (
                            <div
                                key={job.id}
                                draggable={canManage}
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('jobId', job.id);
                                    e.dataTransfer.setData('type', 'unassigned');
                                    onDragStart();
                                }}
                                onDragEnd={onDragEnd}
                                className={`bg-white rounded-lg border border-amber-200 p-3 ${
                                    canManage
                                        ? 'cursor-grab active:cursor-grabbing hover:shadow-sm hover:border-amber-300 transition-all'
                                        : ''
                                }`}
                            >
                                <div className="flex items-start gap-2">
                                    {canManage && (
                                        <GripVertical size={14} className="text-amber-300 flex-shrink-0 mt-0.5" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="text-sm font-semibold text-gray-800 truncate">{job.title}</p>
                                            {preferredCrew && (
                                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 rounded-full font-medium flex-shrink-0 flex items-center gap-1">
                                                    <Users size={9} />
                                                    Prefers {preferredCrew.name}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 truncate mt-0.5">{job.clientName}</p>
                                        {job.address && (
                                            <p className="text-xs text-gray-400 truncate flex items-center gap-1 mt-0.5">
                                                <MapPin size={10} className="flex-shrink-0" />
                                                {job.address}
                                            </p>
                                        )}
                                        {scheduleLabel && (
                                            <p className="text-xs text-blue-600 flex items-center gap-1 mt-0.5">
                                                <Clock size={10} className="flex-shrink-0" />
                                                {scheduleLabel}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {canManage && (
                        <p className="text-xs text-amber-600 text-center pt-1">
                            Select a crew group above, then drag jobs onto the route card to assign
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// FIX BAD ADDRESSES MODAL (Phase 3.5)
//
// Walks the user through fixing addresses that failed pre-flight
// validation. One step per bad address. Handles four response shapes
// from updateJobAddressWithPropagation:
//
//   - ok → mark done, move to next bad address
//   - ambiguous → show alternatives picker
//   - not_found → offer "confirm manually" option
//   - needs_overwrite_confirmation → show inline confirmation
//
// When ALL bad addresses are resolved (or skipped), the modal calls
// onComplete() which re-triggers Optimize.
// ─────────────────────────────────────────────────────────────────────────────
const FixBadAddressesModal = ({ open, badStops, orgId, onClose, onComplete }) => {
    const [currentIdx, setCurrentIdx]         = useState(0);
    const [newAddress, setNewAddress]         = useState('');
    const [propagateToCustomer, setPropagate] = useState(true); // default checked, per design decision
    const [status, setStatus]                 = useState('idle');
    // status: idle | checking | ambiguous | not_found | error
    //         needs_overwrite | confirming_manual | done
    const [errorMsg, setErrorMsg]             = useState('');
    const [alternatives, setAlternatives]     = useState([]);
    const [overwriteInfo, setOverwriteInfo]   = useState(null);
    // overwriteInfo: { address, addressCanonical } — what would be overwritten

    const currentStop = badStops[currentIdx];

    // Reset state when the current stop changes (moving to next bad address)
    useEffect(() => {
        if (!currentStop) return;
        setNewAddress(currentStop.currentAddress || '');
        setPropagate(true);
        setStatus('idle');
        setErrorMsg('');
        setAlternatives([]);
        setOverwriteInfo(null);
    }, [currentIdx, currentStop?.jobId]);

    // Reset when modal opens fresh (with a new batch of stops)
    useEffect(() => {
        if (!open) return;
        setCurrentIdx(0);
    }, [open]);

    if (!open || badStops.length === 0) return null;
    if (!currentStop) return null;

    const totalSteps = badStops.length;
    const stepNum    = currentIdx + 1;

    // Submit a fix attempt. The CF returns one of several shapes.
    const submitFix = async (extraFlags = {}) => {
        if (!newAddress.trim()) return;
        setStatus('checking');
        setErrorMsg('');
        try {
            const fn = httpsCallable(functions, 'updateJobAddressWithPropagation');
            const result = await fn({
                orgId,
                jobId: currentStop.jobId,
                newAddress: newAddress.trim(),
                propagateToCustomer,
                confirmedManually: extraFlags.confirmedManually || false,
                overwriteVerifiedCustomer: extraFlags.overwriteVerifiedCustomer || false,
            });
            const data = result.data;
            if (data.status === 'ok') {
                // Done with this stop. Move to next or complete.
                if (currentIdx + 1 < totalSteps) {
                    setCurrentIdx(currentIdx + 1);
                } else {
                    setStatus('done');
                    onComplete(); // Triggers re-run of optimize
                }
            } else if (data.status === 'ambiguous') {
                setStatus('ambiguous');
                setAlternatives(data.alternatives || []);
            } else if (data.status === 'not_found') {
                setStatus('not_found');
            } else if (data.status === 'needs_overwrite_confirmation') {
                setStatus('needs_overwrite');
                setOverwriteInfo(data.customerCurrent || null);
            } else if (data.status === 'error') {
                setStatus('error');
                setErrorMsg(data.message || 'Verification failed. Try again.');
            }
        } catch (err) {
            setStatus('error');
            const code = err?.code || '';
            setErrorMsg(code === 'functions/resource-exhausted'
                ? 'Rate limit reached. Please try again in a few minutes.'
                : 'Verification temporarily unavailable.');
        }
    };

    const handlePickAlternative = (alt) => {
        setNewAddress(alt.canonical);
        // Re-submit with the picked canonical
        // Need to wait for state update — use a microtask
        setTimeout(() => submitFix(), 0);
    };

    const handleConfirmManually = () => {
        submitFix({ confirmedManually: true });
    };

    const handleConfirmOverwrite = () => {
        submitFix({ overwriteVerifiedCustomer: true });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                            <h2 className="text-lg font-semibold text-gray-900">Fix addresses before optimizing</h2>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            Step {stepNum} of {totalSteps} — {currentStop.customerName}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    >
                        <XIcon size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-4">
                    {/* Current bad address */}
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                        <div className="flex items-start gap-2 text-xs text-red-700">
                            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <div className="font-semibold text-red-800">Current address can't be used</div>
                                <div className="mt-1 break-words font-mono text-red-700">
                                    {currentStop.currentAddress || '(empty)'}
                                </div>
                                <div className="mt-1.5 text-red-600 italic">
                                    {currentStop.message || 'This address needs to be corrected before driving.'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Input for corrected address */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Corrected address
                        </label>
                        <input
                            type="text"
                            value={newAddress}
                            onChange={e => {
                                setNewAddress(e.target.value);
                                if (status !== 'idle' && status !== 'checking') {
                                    setStatus('idle');
                                    setErrorMsg('');
                                    setAlternatives([]);
                                    setOverwriteInfo(null);
                                }
                            }}
                            disabled={status === 'checking'}
                            placeholder="e.g. 123 Main St, Thunder Bay, ON"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                            autoFocus
                        />
                    </div>

                    {/* Status panels */}
                    {status === 'ambiguous' && alternatives.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
                            <div className="flex items-center gap-2 text-xs text-amber-800 font-semibold">
                                <AlertTriangle size={13} />
                                <span>Google found a different address</span>
                            </div>
                            <p className="text-xs text-amber-700">
                                Pick the right match below, or confirm your typed address as-is.
                            </p>
                            <div className="space-y-1">
                                {alternatives.map((alt, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handlePickAlternative(alt)}
                                        className="w-full text-left text-sm px-3 py-2 bg-white border border-amber-200 rounded hover:border-amber-400 hover:bg-amber-50 flex items-start gap-2"
                                    >
                                        <MapPin size={12} className="flex-shrink-0 mt-0.5 text-amber-600" />
                                        <span className="break-words flex-1">{alt.canonical}</span>
                                    </button>
                                ))}
                            </div>
                            <div className="pt-2 border-t border-amber-200">
                                <button
                                    type="button"
                                    onClick={handleConfirmManually}
                                    className="w-full px-3 py-2 bg-white border border-amber-300 text-amber-700 text-sm font-semibold rounded hover:bg-amber-100"
                                >
                                    None of these — use my typed address anyway
                                </button>
                                <p className="text-[11px] text-amber-600 mt-1.5 text-center">
                                    Use this for rural addresses Google can't pinpoint, or when you're sure your address is correct.
                                </p>
                            </div>
                        </div>
                    )}

                    {status === 'not_found' && (
                        <div className="bg-red-50 border border-red-200 rounded p-3 space-y-3">
                            <div className="flex items-start gap-2 text-xs text-red-700">
                                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-semibold">Google can't find this address</div>
                                    <div className="mt-1 text-red-600">
                                        If this address is correct (e.g., a rural property Google hasn't indexed yet), you can confirm it manually.
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleConfirmManually}
                                    className="flex-1 px-3 py-2 bg-white border border-red-300 text-red-700 text-sm font-semibold rounded hover:bg-red-100"
                                >
                                    Confirm this address is correct
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatus('idle')}
                                    className="flex-1 px-3 py-2 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700"
                                >
                                    Try different
                                </button>
                            </div>
                        </div>
                    )}

                    {status === 'needs_overwrite' && overwriteInfo && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-3">
                            <div className="flex items-start gap-2 text-xs text-amber-800">
                                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-semibold">Replace customer's verified address?</div>
                                    <div className="mt-1.5 text-amber-700">
                                        <div className="text-amber-600">Current customer address:</div>
                                        <div className="font-mono break-words">{overwriteInfo.address}</div>
                                    </div>
                                    <div className="mt-1.5 text-amber-700">
                                        <div className="text-amber-600">Replace with:</div>
                                        <div className="font-mono break-words font-semibold">{newAddress.trim()}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleConfirmOverwrite}
                                    className="flex-1 px-3 py-2 bg-amber-600 text-white text-sm font-semibold rounded hover:bg-amber-700"
                                >
                                    Yes, replace customer record
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        // Don't propagate — just fix the job
                                        setPropagate(false);
                                        setTimeout(() => submitFix(), 0);
                                    }}
                                    className="flex-1 px-3 py-2 bg-white border border-amber-300 text-amber-700 text-sm font-semibold rounded hover:bg-amber-100"
                                >
                                    Update job only
                                </button>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="bg-red-50 border border-red-200 rounded p-3 flex items-start gap-2 text-xs text-red-700">
                            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                            <span>{errorMsg}</span>
                        </div>
                    )}

                    {/* Propagate-to-customer checkbox */}
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={propagateToCustomer}
                            onChange={e => setPropagate(e.target.checked)}
                            disabled={status === 'checking'}
                            className="mt-0.5"
                        />
                        <span className="text-sm text-gray-700">
                            Also update <span className="font-semibold">{currentStop.customerName}</span>'s customer record (and any other pending jobs at this address)
                        </span>
                    </label>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                    >
                        Cancel optimize
                    </button>
                    <button
                        onClick={() => submitFix()}
                        disabled={status === 'checking' || !newAddress.trim() || status === 'ambiguous' || status === 'not_found' || status === 'needs_overwrite'}
                        className="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                    >
                        {status === 'checking' ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                <span>Verifying…</span>
                            </>
                        ) : (
                            <span>{stepNum < totalSteps ? 'Save & next' : 'Save & optimize'}</span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const RoutesView = ({ currentUserProfile, employees, openJobEditorModal, onRequestJobStart }) => {
    const [searchParams] = useSearchParams();
    const [selectedDate, setSelectedDate]       = useState(() => searchParams.get('date') || toDateInput(new Date()));
    const [routeGroups, setRouteGroups]         = useState([]);
    const [fieldWorkers, setFieldWorkers]       = useState([]);
    const [customers, setCustomers]             = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [jobs, setJobs]                       = useState([]);
    const [crewJobs, setCrewJobs]               = useState({});

    // ── Phase 3.5: Bad-address modal state ─────────────────────────────────
    const [fixModalOpen, setFixModalOpen]       = useState(false);
    const [badStops, setBadStops]               = useState([]);
    // Set true to skip the pre-flight check and run optimize directly. Used
    // when the modal completes a fix cycle so we don't infinite-loop the
    // check (in case the CF state is somehow stale).
    const [skipPreflightOnce, setSkipPreflightOnce] = useState(false);
    const [crewMembers, setCrewMembers]         = useState([]);
    const [isOptimizing, setIsOptimizing]       = useState(false);
    const [isOptimized, setIsOptimized]         = useState(false);
    const [isSaving, setIsSaving]               = useState(false);
    const [routeHealth, setRouteHealth]         = useState(null);
    const [showHealth, setShowHealth]           = useState(true);
    const [showMap, setShowMap]                 = useState(false);
    const [driveTimesMap, setDriveTimesMap]     = useState({});
    const [savedMsg, setSavedMsg]               = useState('');
    const [savedRouteLoaded, setSavedRouteLoaded] = useState(false);
    const savedRouteRef = useRef(null); // stores saved job order as string for dirty check
    const [isExternalDragging, setIsExternalDragging] = useState(false);
    const jobsLoadedRef = useRef(false);

    // ── Starting point state ────────────────────────────────────────────────
    // Shape: { mode: 'depot'|'current'|'custom', address?, lat?, lng?, label } or null
    const [startingPoint, setStartingPoint] = useState(null);

    const mapRef = useRef(null);
    const googleMapRef = useRef(null);
    const markersRef = useRef([]);
    const polylinesRef = useRef([]);

    const orgId = currentUserProfile?.organizationId;
    const canManage = can(currentUserProfile, PERMISSIONS.MANAGE_ROUTES);

    // ── Fetch route groups ──────────────────────────────────────────────────
    useEffect(() => {
        if (!orgId) return;
        const q = query(collection(db, `organizations/${orgId}/routeGroups`), orderBy('name'));
        return onSnapshot(q, snap => {
            const groups = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setRouteGroups(groups);
            if (groups.length > 0 && !selectedGroupId) setSelectedGroupId(groups[0].id);
        });
    }, [orgId]);

    // ── Fetch field workers ─────────────────────────────────────────────────
    useEffect(() => {
        if (!orgId) return;
        const q = query(collection(db, `organizations/${orgId}/fieldWorkers`), where('status', '==', 'active'));
        return onSnapshot(q, snap => setFieldWorkers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    }, [orgId]);

    // ── Fetch customers (for preferred crew lookup) ─────────────────────────
    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(
            collection(db, `organizations/${orgId}/customers`),
            snap => setCustomers(snap.docs.map(d => ({ id: d.id, preferredCrewId: d.data().preferredCrewId || null })))
        );
    }, [orgId]);

    // ── Preferred crew map ──────────────────────────────────────────────────
    const preferredCrewMap = useMemo(() => {
        const map = {};
        customers.forEach(c => {
            if (c.preferredCrewId) map[c.id] = c.preferredCrewId;
        });
        return map;
    }, [customers]);

    // ── Resolve crew members when group changes ─────────────────────────────
    useEffect(() => {
        if (!selectedGroupId) { setCrewMembers([]); return; }
        const group = routeGroups.find(g => g.id === selectedGroupId);
        if (!group) { setCrewMembers([]); return; }
        const allWorkers = [...(employees || []), ...fieldWorkers];
        const members = (group.memberIds || [])
            .map(id => allWorkers.find(w => w.id === id))
            .filter(Boolean);
        setCrewMembers(members);
    }, [selectedGroupId, routeGroups, employees, fieldWorkers]);

    // ── Initialize starting point from route group depot when group changes ─
    // Only runs when the user actually switches groups (not when the
    // routeGroups Firestore subscription re-emits, which would clobber any
    // custom/current-location pick the user made).
    const initialisedGroupRef = useRef(null);
    useEffect(() => {
        if (!selectedGroupId) {
            setStartingPoint(null);
            initialisedGroupRef.current = null;
            return;
        }
        if (initialisedGroupRef.current === selectedGroupId) return;
        const group = routeGroups.find(g => g.id === selectedGroupId);
        // Wait for the group's data to actually load before initialising
        if (!group) return;
        initialisedGroupRef.current = selectedGroupId;
        const depot = group.defaultStartingAddress?.trim();
        if (depot) {
            setStartingPoint({ mode: 'depot', address: depot, label: depot });
        } else {
            setStartingPoint(null);
        }
    }, [selectedGroupId, routeGroups]);

    // ── Fetch jobs for selected date ────────────────────────────────────────
    useEffect(() => {
        if (!orgId || !selectedDate) return;
        const start = startOfDay(new Date(selectedDate + 'T00:00:00'));
        const end   = endOfDay(new Date(selectedDate + 'T00:00:00'));

        const q = query(
            collection(db, `organizations/${orgId}/jobs`),
            where('startAt', '>=', start),
            where('startAt', '<=', end),
        );

        return onSnapshot(q, snap => {
            const all = snap.docs
                .map(d => {
                    const data = d.data();
                    return {
                        id: d.id, jobId: d.id, ...data,
                        startAt: data.startAt?.toDate ? data.startAt.toDate() : data.startAt,
                        endAt:   data.endAt?.toDate   ? data.endAt.toDate()   : data.endAt,
                    };
                })
                .filter(job => job.status !== 'Canceled' && job.status !== 'Completed');
            setJobs(all);
            setIsOptimized(false);
            setRouteHealth(null);
            setShowHealth(true);
            setSavedRouteLoaded(false);
        savedRouteRef.current = null;
            jobsLoadedRef.current = false;
        });
    }, [orgId, selectedDate]);

    // ── Unassigned jobs (no crew assignment, not in any route group) ────────
    const unassignedJobs = useMemo(() => {
        return jobs.filter(job => {
            const hasAssignment = (job.assignedTo || []).length > 0 ||
                !!job.routeGroupId ||
                (Array.isArray(job.crews) && job.crews.length > 0);
            return !hasAssignment;
        });
    }, [jobs]);

    // ── Build crewJobs from raw jobs ────────────────────────────────────────
    useEffect(() => {
        if (!selectedGroupId) { setCrewJobs({}); return; }
        const memberIds = new Set(crewMembers.map(m => m.id));
        const seen = new Set();
        const groupJobs = [];

        jobs.forEach(job => {
            if (seen.has(job.id)) return;
            const allAssigned = [...new Set([
                ...(job.assignedTo || []),
                ...(job.assignedFieldWorkerIds || []),
                ...(job.assignedGroupMemberIds || []),
            ])];
            // Multi-crew: job is in this crew's route if it has a crews[] entry for this group
            const isInMultiCrew = Array.isArray(job.crews) && job.crews.some(c => c.crewGroupId === selectedGroupId);

            if (allAssigned.some(id => memberIds.has(id)) || job.routeGroupId === selectedGroupId || isInMultiCrew) {
                seen.add(job.id);
                // Find this crew's specific line items if multi-crew
                const thisCrewEntry = isInMultiCrew ? job.crews.find(c => c.crewGroupId === selectedGroupId) : null;
                groupJobs.push({
                    jobId: job.id, title: job.title,
                    clientName: job.clientName || '',
                    address: job.address || '',
                    startAt: job.startAt,
                    endAt: job.endAt,
                    schedulingMode: job.schedulingMode,
                    arrivalWindow: job.arrivalWindow,
                    status: job.status,
                    description: job.description || '',
                    clientId: job.clientId || '',
                    // Multi-crew metadata for route card display
                    isMultiCrew: isInMultiCrew,
                    multiCrewTotal: isInMultiCrew ? job.crewsTotal : undefined,
                    multiCrewCompleted: isInMultiCrew ? job.crewsCompleted : undefined,
                    thisCrewCompleted: thisCrewEntry?.completedAt ? true : false,
                    otherCrews: isInMultiCrew
                        ? (job.crews || []).filter(c => c.crewGroupId !== selectedGroupId).map(c => c.crewGroupName)
                        : [],
                });
            }
        });

        setCrewJobs({ [selectedGroupId]: groupJobs });
        jobsLoadedRef.current = true;
    }, [jobs, crewMembers, selectedGroupId]);

    // ── Load saved route order after crewJobs is built ──────────────────────
    useEffect(() => {
        if (!orgId || !selectedDate || !selectedGroupId) return;
        if (!jobsLoadedRef.current) return;
        if (isOptimized) return;
        if (savedRouteLoaded) return;

        const routeId = `${selectedDate}_${selectedGroupId}`;
        getDoc(doc(db, `organizations/${orgId}/savedRoutes`, routeId)).then(snap => {
            if (!snap.exists()) return;
            const saved = snap.data();
            if (!saved.crewJobs) return;

            setCrewJobs(current => {
                const merged = {};
                Object.entries(current).forEach(([memberId, currentJobs]) => {
                    const savedOrder = saved.crewJobs[memberId] || [];
                    const savedIds = savedOrder.map(j => j.jobId || j.id);
                    const reordered = [
                        ...savedIds.map(id => currentJobs.find(j => j.jobId === id)).filter(Boolean),
                        ...currentJobs.filter(j => !savedIds.includes(j.jobId)),
                    ];
                    merged[memberId] = reordered;
                });
                // Store synchronously in ref so button comparison is always accurate
                savedRouteRef.current = JSON.stringify(merged);
                return merged;
            });
            // Restore the saved starting point if there is one
            if (saved.startingPoint && typeof saved.startingPoint === 'object') {
                setStartingPoint(saved.startingPoint);
            }
            setSavedRouteLoaded(true);
            setIsOptimized(true);
        }).catch(err => {
            console.warn('Could not load saved route:', err);
        });
    }, [orgId, selectedDate, selectedGroupId, crewJobs, isOptimized, savedRouteLoaded]);

    // ── Optimize (Phase 3.5: with address pre-flight) ──────────────────────
    const handleOptimize = async () => {
        if (!orgId || !selectedGroupId || crewMembers.length === 0) return;

        // Skip pre-flight if we just came back from a fix cycle.
        if (skipPreflightOnce) {
            setSkipPreflightOnce(false);
            return runOptimizeAfterFixes();
        }

        // Build a flat list of jobIds across all crews in the current selection.
        const allJobIds = [];
        Object.values(crewJobs).forEach(jobs => {
            (jobs || []).forEach(j => { if (j.jobId) allJobIds.push(j.jobId); });
        });

        if (allJobIds.length === 0) {
            // Nothing to optimize. Just bail.
            return;
        }

        setIsOptimizing(true);
        setRouteHealth(null);

        // Pre-flight check
        try {
            const prepareFn = httpsCallable(functions, 'prepareOptimizeAddresses');
            const preflightResult = await prepareFn({ orgId, jobIds: allJobIds });
            const { needsAttention = [] } = preflightResult.data || {};

            if (needsAttention.length > 0) {
                // Halt optimize, open the fix modal
                setBadStops(needsAttention);
                setFixModalOpen(true);
                setIsOptimizing(false);
                return;
            }

            // All stops verified or backfilled — proceed with optimize
            await runOptimizeAfterFixes();
        } catch (err) {
            console.error('Pre-flight check failed:', err);
            // If pre-flight fails, fall back to running optimize directly
            // so the user isn't blocked. They might see phantom-address
            // issues, but that's no worse than before Phase 3.5.
            console.warn('Pre-flight unavailable, running optimize anyway');
            await runOptimizeAfterFixes();
        }
    };

    // The actual optimize call — extracted so it can be called from either
    // the happy path (handleOptimize after pre-flight clean) or after the
    // fix modal completes.
    const runOptimizeAfterFixes = async () => {
        setIsOptimizing(true);
        setRouteHealth(null);
        try {
            const optimizeRoutes = httpsCallable(functions, 'optimizeRoutes');
            const payload = { orgId, date: selectedDate, crewJobs };
            if (startingPoint) {
                const sp = {};
                if (startingPoint.address) sp.address = startingPoint.address;
                if (typeof startingPoint.lat === 'number') sp.lat = startingPoint.lat;
                if (typeof startingPoint.lng === 'number') sp.lng = startingPoint.lng;
                if (startingPoint.label) sp.label = startingPoint.label;
                if (Object.keys(sp).length > 0) payload.startingPoint = sp;
            }
            const result = await optimizeRoutes(payload);
            const { results, routeHealth: health } = result.data;

            const updated = { ...crewJobs };
            const newDriveTimes = {};
            Object.entries(results).forEach(([groupId, data]) => {
                updated[groupId] = data.optimizedJobs;
                newDriveTimes[groupId] = data.driveTimeMinutes;
            });
            setCrewJobs(updated);
            setDriveTimesMap(newDriveTimes);
            setRouteHealth(health);
            setShowHealth(true);
            setIsOptimized(true);
        } catch (err) {
            console.error('Optimization failed:', err);
            alert('Optimization failed: ' + (err.message || 'Unknown error'));
        } finally {
            setIsOptimizing(false);
        }
    };

    // ── Handle manual drag reorder ──────────────────────────────────────────
    const handleDragEnd = useCallback((groupId, newOrder) => {
        setCrewJobs(prev => ({ ...prev, [groupId]: newOrder }));
    }, []);

    // ── Assign unassigned job to a crew group ───────────────────────────────
    const handleAssignToGroup = async (jobId, groupId) => {
        const group = routeGroups.find(g => g.id === groupId);
        if (!group || !orgId) return;
        const allWorkers = [...(employees || []), ...fieldWorkers];
        const memberIds = group.memberIds || [];
        const memberNames = memberIds
            .map(id => allWorkers.find(w => w.id === id)?.name || '')
            .filter(Boolean);
        try {
            await updateDoc(doc(db, `organizations/${orgId}/jobs`, jobId), {
                assignedTo: memberIds,
                assignedToNames: memberNames,
                assignedGroupMemberIds: memberIds,
                routeGroupId: groupId,
                routeGroupName: group.name || '',
                assignmentMode: 'group',
            });
        } catch (err) {
            console.error('Failed to assign job to group:', err);
            alert('Could not assign job. Please try again.');
        }
    };

    // ── Save route order ────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!orgId) return;
        setIsSaving(true);
        try {
            const routeId = `${selectedDate}_${selectedGroupId}`;
            // Strip undefined values — Firestore rejects them
            const sanitizedCrewJobs = JSON.parse(JSON.stringify(crewJobs));
            const payload = {
                date: selectedDate,
                routeGroupId: selectedGroupId,
                crewJobs: sanitizedCrewJobs,
                savedAt: serverTimestamp(),
                savedBy: currentUserProfile.id,
            };
            // Persist starting point if set, so reload restores the picker state
            if (startingPoint) {
                payload.startingPoint = JSON.parse(JSON.stringify(startingPoint));
            }
            await setDoc(doc(db, `organizations/${orgId}/savedRoutes`, routeId), payload);
            setSavedMsg('Route saved!');
            setSavedRouteLoaded(true);
            savedRouteRef.current = JSON.stringify(crewJobs);
            setTimeout(() => setSavedMsg(''), 3000);
        } catch (err) {
            console.error('Save failed:', err);
            alert('Failed to save route: ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    // ── Map rendering ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!showMap || !mapRef.current) return;
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!apiKey) return;
        const initMap = () => {
            if (!window.google || !mapRef.current) return;
            const map = new window.google.maps.Map(mapRef.current, {
                zoom: 12,
                center: { lat: 48.3809, lng: -89.2477 },
                mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
            });
            googleMapRef.current = map;
            renderMapMarkers();
        };
        if (!window.google) {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
            script.async = true;
            script.onload = initMap;
            document.head.appendChild(script);
        } else {
            initMap();
        }
    }, [showMap]);

    const renderMapMarkers = useCallback(() => {
        if (!googleMapRef.current || !window.google) return;
        markersRef.current.forEach(m => m.setMap(null));
        polylinesRef.current.forEach(p => p.setMap(null));
        markersRef.current = [];
        polylinesRef.current = [];
    }, [crewJobs, crewMembers]);

    useEffect(() => {
        if (showMap && googleMapRef.current) renderMapMarkers();
    }, [crewJobs, showMap, renderMapMarkers]);

    const selectedGroup = routeGroups.find(g => g.id === selectedGroupId);
    const totalJobs = (crewJobs[selectedGroupId] || []).length;
    const groupDepot = selectedGroup?.defaultStartingAddress?.trim() || '';

    return (
        <div className="p-4 sm:p-6 md:p-8 w-full min-w-0">
            {/* ── PAGE HEADER ─────────────────────────────────────────── */}
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Routes</h1>
                    <p className="text-sm text-gray-500 mt-1">Plan and optimize crew routes for the day.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {savedMsg && (
                        <span className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle size={14} /> {savedMsg}
                        </span>
                    )}
                    {canManage && isOptimized && (() => {
                        const hasUnsavedChanges = savedRouteRef.current !== JSON.stringify(crewJobs);
                        return (
                            <button
                                onClick={handleSave}
                                disabled={isSaving || !hasUnsavedChanges}
                                title={!hasUnsavedChanges ? 'No changes to save' : 'Save current route order'}
                                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors
                                    ${hasUnsavedChanges
                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                    } disabled:opacity-50`}
                            >
                                <Save size={15} />
                                {isSaving ? 'Saving…' : hasUnsavedChanges ? 'Save Route' : 'Saved ✓'}
                            </button>
                        );
                    })()}
                </div>
            </div>

            {/* ── CONTROLS BAR ────────────────────────────────────────── */}
            <div className="flex flex-wrap items-end gap-3 mb-5">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                        <Calendar size={12} /> Date
                    </label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={e => { setSelectedDate(e.target.value); setIsOptimized(false); setSavedRouteLoaded(false); }}
                        className="p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div className="flex-1 min-w-[160px]">
                    <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                        <Users size={12} /> Route Group
                    </label>
                    {routeGroups.length === 0 ? (
                        <p className="text-xs text-gray-400 italic py-2">No route groups. Create them in Settings → Route Groups.</p>
                    ) : (
                        <div className="relative">
                            <select
                                value={selectedGroupId}
                                onChange={e => { setSelectedGroupId(e.target.value); setIsOptimized(false); setSavedRouteLoaded(false); }}
                                className="w-full appearance-none p-2 pr-8 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                                <option value="">— Select a group —</option>
                                {routeGroups.map(g => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                    )}
                </div>

                {canManage && (
                    <button
                        onClick={handleOptimize}
                        disabled={isOptimizing || !selectedGroupId || crewMembers.length === 0}
                        className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                        {isOptimizing ? (
                            <><RotateCcw size={15} className="animate-spin" /> Optimizing…</>
                        ) : (
                            <><Zap size={15} /> Optimize Routes</>
                        )}
                    </button>
                )}

                <button
                    onClick={() => setShowMap(p => !p)}
                    className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                    {showMap ? <EyeOff size={15} /> : <Eye size={15} />}
                    {showMap ? 'Hide Map' : 'Show Map'}
                </button>
            </div>

            {/* ── STARTING POINT BAR ─────────────────────────────────── */}
            {selectedGroupId && crewMembers.length > 0 && (
                <StartingPointBar
                    startingPoint={startingPoint}
                    groupDepot={groupDepot}
                    onChange={(sp) => { setStartingPoint(sp); setIsOptimized(false); }}
                    onClear={() => { setStartingPoint(null); setIsOptimized(false); }}
                    canManage={canManage}
                    functions={functions}
                />
            )}

            {/* ── SAVED ROUTE BANNER ──────────────────────────────────── */}
            {savedRouteLoaded && !isOptimizing && (
                <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                    <BookOpen size={13} className="flex-shrink-0" />
                    <span>Showing your saved route order for this day. Hit <strong>Optimize Routes</strong> to recalculate, or drag stops to adjust.</span>
                </div>
            )}

            {/* ── SUMMARY STRIP ───────────────────────────────────────── */}
            {selectedGroup && (
                <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                        <Users size={12} />
                        <strong>{crewMembers.length}</strong> crew {crewMembers.length === 1 ? 'member' : 'members'}
                    </span>
                    <span className="text-gray-300">·</span>
                    <span className="flex items-center gap-1">
                        <MapPin size={12} />
                        <strong>{totalJobs}</strong> {totalJobs === 1 ? 'job' : 'jobs'} scheduled
                    </span>
                    {isOptimized && (
                        <>
                            <span className="text-gray-300">·</span>
                            <span className="flex items-center gap-1 text-green-600">
                                <Zap size={12} />
                                {savedRouteLoaded ? 'Saved order restored' : 'Optimized'}
                            </span>
                        </>
                    )}
                </div>
            )}

            {/* ── ROUTE HEALTH CARD ───────────────────────────────────── */}
            {showHealth && routeHealth && (
                <RouteHealthCard health={routeHealth} onDismiss={() => setShowHealth(false)} />
            )}

            {/* ── MAP ─────────────────────────────────────────────────── */}
            {showMap && (
                <div className="mb-5 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                    <div ref={mapRef} style={{ height: 320 }} className="w-full bg-gray-100">
                        <div className="h-full flex items-center justify-center text-sm text-gray-400">
                            <div className="text-center">
                                <Map size={32} className="mx-auto mb-2 text-gray-300" />
                                <p>Map loading…</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── EMPTY STATES ─────────────────────────────────────────── */}
            {!selectedGroupId && (
                <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
                    <Map size={36} className="text-gray-300 mx-auto mb-3" />
                    <h3 className="text-sm font-semibold text-gray-500 mb-1">Select a route group to get started</h3>
                    <p className="text-xs text-gray-400">Create groups in <strong>Settings → Route Groups</strong> then come back here to plan your day.</p>
                </div>
            )}

            {selectedGroupId && crewMembers.length === 0 && (
                <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
                    <Users size={36} className="text-gray-300 mx-auto mb-3" />
                    <h3 className="text-sm font-semibold text-gray-500 mb-1">This group has no members</h3>
                    <p className="text-xs text-gray-400">Add team members to this group in Settings → Route Groups.</p>
                </div>
            )}

            {/* ── UNASSIGNED JOBS PANEL ────────────────────────────────── */}
            {selectedGroupId && crewMembers.length > 0 && (
                <UnassignedJobsPanel
                    jobs={unassignedJobs}
                    routeGroups={routeGroups}
                    preferredCrewMap={preferredCrewMap}
                    onDragStart={() => setIsExternalDragging(true)}
                    onDragEnd={() => setIsExternalDragging(false)}
                    canManage={canManage}
                />
            )}

            {/* ── CREW CARD — one card for the whole group ─────────────── */}
            {crewMembers.length > 0 && selectedGroup && (
                <div className="max-w-2xl">
                    <CrewCard
                        group={selectedGroup}
                        members={crewMembers}
                        jobs={crewJobs[selectedGroupId] || []}
                        color={selectedGroup?.color || CREW_COLORS[0]}
                        driveTimeMinutes={driveTimesMap[selectedGroupId] || 0}
                        onDragEnd={handleDragEnd}
                        isOptimized={isOptimized}
                        isExternalDragging={isExternalDragging}
                        onExternalDrop={handleAssignToGroup}
                        startingPoint={startingPoint}
                        onJobClick={async (job) => {
                            if (onRequestJobStart) {
                                try {
                                    const snap = await getDoc(doc(db, `organizations/${orgId}/jobs`, job.jobId));
                                    onRequestJobStart(snap.exists() ? { ...snap.data(), id: snap.id } : { id: job.jobId, ...job }); // s6costing-docid: doc id wins over a stale stored `id` field
                                } catch {
                                    onRequestJobStart({ id: job.jobId, ...job });
                                }
                            } else if (openJobEditorModal) {
                                openJobEditorModal({ id: job.jobId, ...job });
                            }
                        }}
                    />
                </div>
            )}

            {/* ── NO JOBS STATE ────────────────────────────────────────── */}
            {crewMembers.length > 0 && totalJobs === 0 && unassignedJobs.length === 0 && (
                <div className="mt-4 text-center py-8 bg-gray-50 rounded-xl border border-gray-200">
                    <p className="text-sm text-gray-400">No jobs scheduled for this crew on {selectedDate}.</p>
                    {canManage && (
                        <button
                            onClick={() => openJobEditorModal && openJobEditorModal()}
                            className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                            + Add a job
                        </button>
                    )}
                </div>
            )}

            {/* Phase 3.5: Fix bad addresses modal */}
            <FixBadAddressesModal
                open={fixModalOpen}
                badStops={badStops}
                orgId={orgId}
                onClose={() => {
                    setFixModalOpen(false);
                    setBadStops([]);
                    // User cancelled — don't run optimize
                }}
                onComplete={() => {
                    setFixModalOpen(false);
                    setBadStops([]);
                    // All fixes applied — re-trigger optimize (skipping pre-flight this time)
                    setSkipPreflightOnce(true);
                    // Use a microtask so state has settled before handleOptimize reads it
                    setTimeout(() => handleOptimize(), 0);
                }}
            />
        </div>
    );
};

export default RoutesView;